import { includes, compact } from 'lodash'
import creditCardTypes from './../data/creditCardTypes'

class ParameterService {
    constructor() {
        this.loadParameters();
    }

    getParameterByName(name) {
        const url = window.location.href;
        name = name.replace(/[[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i');
        const results = regex.exec(url);
        if (!results) {
            return '';
        }
        if (!results[2]) {
            return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    getHiddenInputValue(inputName) {
        const element = document.getElementsByName(inputName)[0];
        if (!(element instanceof Object && element.value)) {
            return '';
        }
        return element.value.toString();
    }

    getAllHiddenInputs() {
        const allInputs = document.getElementsByTagName('input');
        const arr = [];

        for (let i = 0; i < allInputs.length; i++) {
            arr.push(allInputs[i]);
        }

        return arr.filter(input => {
            return input.type === 'hidden';
        });
    }

    /**
     * Browser informations have to be sent along with the request for Visa 3D secure
     * @returns {string}
     */
    gatherBrowserInfos() {
        const javaEnabled = !!navigator.JavaEnabled;
        const language = navigator.language || navigator.userLanguage;
        const colorDepth = window.screen.colorDepth;
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        const userAgent = navigator.userAgent;
        const timeZoneOffset = new Date().getTimezoneOffset().toString();

        const payload = {
            javaEnabled,
            javaScriptEnabled: true,
            language,
            colorDepth,
            screenHeight,
            screenWidth,
            timeZoneOffset,
            userAgent
        };

        return btoa(JSON.stringify(payload));
    }

    loadData(validKeys) {
        const data = {};

        this.hiddenInputs = this.getAllHiddenInputs();

        this.hiddenInputs.forEach(input => {
            if (validKeys.includes(input.name)) {
                data[input.name] = this.getHiddenInputValue(input.name);
            }
        });

        return data;
    }

    loadPrefillData() {
        const validPrefillKeys = [
            'Brand',
            'CardNumber',
            'MaskedNumber',
            'ExpiryMonth',
            'ExpiryYear',
            'Cardholder',
        ];

        return this.loadData(validPrefillKeys);
    }

    loadApplePayData() {
        const validApplePayKeys = [
            'ApplePayAmount',
            'ApplePayCurrency',
            'ApplePayCountryCode',
            'ApplePayBrands',
            'ApplePayPostURL',
            'ApplePayValidationURL',
            'ApplePayPayID',
            'ApplePayPaymentToken',
        ];

        return this.loadData(validApplePayKeys);
    }

    loadGooglePayData() {
        const validGooglePayKeys = [
            'GooglePayEnvironment',
            'GooglePayGatewayMerchantId',
            'GooglePayMerchantName',
            'GooglePayMerchantOrigin',
            'GooglePayAllowedCardNetworks',
            'GooglePayAllowedCardAuthMethods',
            'GooglePayCountryCode',
            'GooglePayTotalPrice',
            'GooglePayCurrencyCode',
            'GooglePayPostURL',
        ];

        return this.loadData(validGooglePayKeys);
    }

    loadPaygateData() {
        const validPaygateKeys = [
            'MerchantID',
            'Len',
            'Data',
            'DataSmall',
            'counter',
            'blnFirst',
            'Template',
            'URLBack',
            'Language',
            'PaymentMethod',
            'CreditCardBrand',
            'PayTypes',
            'CCTypes',
            'ErrorMessage',
            'binfo',
            'prefill'
        ];
        const paygateData = this.loadData(validPaygateKeys);

        // Add browser infos
        paygateData.browserInfo = this.gatherBrowserInfos();

        return paygateData;
    }

    loadParameters() {
        this.data = {
            amount: this.getHiddenInputValue('customfield1'),
            orderId: this.getHiddenInputValue('customfield2'),
            merchantLogo: this.getHiddenInputValue('customfield3'),
            shoppingCart: this.getHiddenInputValue('customfield4').replace(/\|/g, '\n'),
            userData: this.getHiddenInputValue('customfield5').replace(/\|/g, '\n'),
            shippingDetails: this.getHiddenInputValue('customfield6').replace(/\|/g, '\n'),
            billingDetails: this.getHiddenInputValue('customfield7').replace(/\|/g, '\n'),
            freeTextHeader: this.getHiddenInputValue('customfield8'),
            freeTextValue: this.getHiddenInputValue('customfield9').replace(/\|/g, '\n'),
            saveCardData: this.getHiddenInputValue('SaveCardData'),
        };

        this.getPaygateData();
        this.prefillData = this.loadPrefillData();
        this.applePayData = this.loadApplePayData();
        this.googlePayData = this.loadGooglePayData();

        let configuredPayTypes = this.paygate['PayTypes'] ? this.paygate['PayTypes'] : this.paygate['CCTypes'];
        if (configuredPayTypes) {
            this.payTypes = configuredPayTypes.split('|');
        } else {
            this.payTypes = [];
        }

        const allowedTemplates = ["cards_v1", "cards_v1_test", "ct_cards_v1", "ct_cards_v1_test",  "1cs_cards_v1", "1cs_cards_v1_test", "paymentpagedropdown_v1", "paymentpagedropdown_v1_test", "ct_paymentpagedropdown_v1", "ct_paymentpagedropdown_v1_test", "1cs_paymentpagedropdown_v1", "1cs_paymentpagedropdown_v1_test"];

        // another workaround to have Google-/ApplePay on Template
        if (allowedTemplates.includes(this.paygate['Template'].toLowerCase())) {
            if (Object.keys(this.googlePayData).length) {
                this.payTypes.push('GooglePay');
            }

            if (Object.keys(this.applePayData).length) {
                this.payTypes.push('ApplePay');
            }
        }

        const merchantPayTypesFilter = this.getParameterByName('PayTypes');
        if (merchantPayTypesFilter && merchantPayTypesFilter !== 'CC') {
            this.payTypes = merchantPayTypesFilter.split('|').filter(payType => {
                // ugly workarounds for Alipay, until they fix it on server-side
                return (this.payTypes.indexOf(payType) !== -1 || payType === 'Alipay');
            });
        }

        this.creditCardOnly = this.isCreditCardOnly();
        this.language = this.getDefaultLanguage();
    }

    getDefaultLanguage() {
        const lang = this.paygate.Language || this.paygate.language;

        if (lang) {
            return lang;
        }

        const urlParameterLang = this.getParameterByName('Language') || this.getParameterByName('language')
        if (urlParameterLang) {
            return urlParameterLang;
        }

        let parsedLanguage = navigator.language || navigator.userLanguage || 'en';
        if (parsedLanguage.indexOf('-') !== -1) {
            return parsedLanguage.slice(0, parsedLanguage.indexOf('-'))
        }
        return parsedLanguage;
    }

    customFieldsAreUsed() {
        const customFieldsUsed = [
            !!this.data.orderId,
            !!this.data.shoppingCart,
            !!this.data.userData,
            !!this.data.shippingDetails,
            !!this.data.billingDetails,
            !!this.data.freeTextHeader,
            !!this.data.freeTextValue,
        ];
        return customFieldsUsed.indexOf(true) !== -1;
    }

    isCreditCardOnly() {
        return this.getParameterByName('PayTypes') === 'CC';
    }

    /**
     * Checks, if the PayTypes parameter requested credit cards only
     * @returns {boolean}
     */
    includesOnlyCreditCards() {
        const requestedPaymentMethods = this.getParameterByName('PayTypes').split('|');
        const available = creditCardTypes.map(obj => obj.apiType.split('CC_').join(''));

        const allIncluded = requestedPaymentMethods.map(code => {
            return includes(available, code);
        });

        return requestedPaymentMethods[0] === 'CC' || allIncluded.length === compact(allIncluded).length;
    }

    getPaygateData() {
        this.paygate = this.loadPaygateData();

        return this.paygate;
    }

    getApplePaymentRequest() {
        return {
            currencyCode: this.getHiddenInputValue('ApplePayCurrency'),
            countryCode: this.getHiddenInputValue('ApplePayCountryCode'),
            total: {
                'label': 'Amount',
                'amount': this.getHiddenInputValue('ApplePayAmount')
            },
            supportedNetworks: this.getHiddenInputValue('ApplePayBrands').split(','),
            merchantCapabilities: ['supports3DS']
        };
    }

    getGooglePayEnvironment() {
        if (this.getHiddenInputValue('GooglePayTestAccount') === 'true') {
            return 'TEST'
        } else {
            return 'PRODUCTION'
        }
    }

    getGooglePaymentRequest() {
        return {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: this.getHiddenInputValue('GooglePayAllowedCardAuthMethods').split(','), //e.g. ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: this.getHiddenInputValue('GooglePayAllowedCardNetworks').split(','), // e.g. ['MASTERCARD', 'VISA'],
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: 'computop',
                            gatewayMerchantId: this.getHiddenInputValue('MerchantID'),
                        },
                    },
                },
            ],
            merchantInfo: {
                merchantId: this.getHiddenInputValue('GooglePayGatewayMerchantId'),
                merchantName: this.getHiddenInputValue('GooglePayMerchantName'),
            },
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPrice: this.getHiddenInputValue('GooglePayTotalPrice'),
                currencyCode: this.getHiddenInputValue('GooglePayCurrencyCode'),
                countryCode: this.getHiddenInputValue('GooglePayCountryCode'),
            },
        };
    }

    isEmbedMode() {
        /** check hidden input for embed-mode */
        const postEmbed = this.getHiddenInputValue('CustomField14') === 'iframe';
        /** check get-param for embed-mode */
        const getEmbed = this.getParameterByName('CustomField14') === 'iframe';
        /** if any condition is true, trigger embed mode */
        return postEmbed || getEmbed;
    }
}

export default new ParameterService();



// WEBPACK FOOTER //
// ./src/services/parameterService.js